/**
 * AutocompleteEmpty — shared types, Zod prop schema, and styling tokens.
 *
 * The no-results card an Autocomplete shows in place of its suggestion rows:
 * a title and a short body, which can carry an inline link (e.g. a help email
 * address). All copy comes from the host.
 *
 * Web only — the native build renders nothing.
 *
 * Source of truth: web registration v7 sheet, Web/Autocomplete empty
 * https://www.figma.com/design/hk9NaJrKRwkoGmmZ5rxbsE (node 99:7515)
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { shadows } from '../../elements/shadows';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';

/** Catalog-facing schema (plain ZodObject so `formatZodType` can introspect it). */
export const autocompleteEmptyPropsSchema = z.object({
  title: z.string().optional(),
  /** Plain-text body; a host needing an inline link composes children instead. */
  body: z.string().optional(),
  testID: z.string().optional(),
});

export type AutocompleteEmptyProps = z.input<typeof autocompleteEmptyPropsSchema>;

export const AUTOCOMPLETE_EMPTY_BACKGROUND = colors.background.container;
export const AUTOCOMPLETE_EMPTY_BORDER_COLOR = colors.stroke.divider;
export const AUTOCOMPLETE_EMPTY_BORDER_WIDTH = sizing.stroke.hairline;
export const AUTOCOMPLETE_EMPTY_RADIUS = radius.xl;
export const AUTOCOMPLETE_EMPTY_PADDING_HORIZONTAL = spacing.lg;
export const AUTOCOMPLETE_EMPTY_PADDING_VERTICAL = spacing.m;
export const AUTOCOMPLETE_EMPTY_GAP = spacing.xs;
// Figma draws this shadow raw (0 5 10, 8% shadow colour) with no bound
// variable; `shadow300` carries the same values.
export const AUTOCOMPLETE_EMPTY_SHADOW = shadows.shadow300;
export const AUTOCOMPLETE_EMPTY_TITLE_TYPOGRAPHY = typography.b1Regular;
export const AUTOCOMPLETE_EMPTY_TITLE_COLOR = colors.text.primary;
// The body is drawn raw at 14/22 in #5a5a5a; `b3Regular` and
// `text.secondary` carry the same values.
export const AUTOCOMPLETE_EMPTY_BODY_TYPOGRAPHY = typography.b3Regular;
export const AUTOCOMPLETE_EMPTY_BODY_COLOR = colors.text.secondary;
